// src/components/EntriesList/EntriesList.js
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { API_URL } from './apiurl';
import { format } from 'date-fns';
import { fetchAPI } from './EntriesList/utils/apiUtils';
import { formatDateToGerman, parseDate } from './EntriesList/utils/dateUtils';

// Komponenten importieren
import EditEntryModal from './EntriesList/EditEntryModal';
import HistoryModal from './EntriesList/HistoryModal';
import FeiertageDialog from './EntriesList/FeiertageDialog';
import { styles } from './EntriesList/styles/entryStyles';

function EntriesList() {
  const { auth } = useContext(AuthContext);

  // STATE
  const [entries, setEntries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingEntry, setEditingEntry] = useState(null);
  const [exportType, setExportType] = useState('standard');
  const [showFeiertageDialog, setShowFeiertageDialog] = useState(false);
  const aktuellesJahr = new Date().getFullYear();
  const [stundensatzJahr, setStundensatzJahr] = useState(0);
  const [hoursForFeiertag, setHoursForFeiertag] = useState(0);
  const [stundensatzFachgebiet, setStundensatzFachgebiet] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyRecords, setHistoryRecords] = useState([]);
  const [selectedEntryId, setSelectedEntryId] = useState(null);

  // Daten laden
  useEffect(() => {
    fetchEntries();
    fetchSettings();
    fetchCustomers();
  }, [auth.token]);

  const fetchEntries = async () => {
    try {
      const data = await fetchAPI(`${API_URL}/entries`, {
        headers: { 'Authorization': `Bearer ${auth.token}` },
      }, 'Fehler beim Laden der Einträge.');
      setEntries(data);
    } catch (err) {
      toast.error(err.message || 'Serverfehler.');
    }
  };

  const fetchCustomers = async () => {
    try {
      const data = await fetchAPI(`${API_URL}/customers`, {
        headers: { 'Authorization': `Bearer ${auth.token}` },
      }, 'Fehler beim Laden der Kunden.');
      setCustomers(data);
    } catch (err) {
      toast.error(err.message || 'Fehler beim Laden der Kunden.');
    }
  };

  const fetchSettings = async () => {
    try {
      const data = await fetchAPI(`${API_URL}/settings`, {
        headers: { 'Authorization': `Bearer ${auth.token}` },
      }, 'Fehler beim Laden der Einstellungen.');
      setHoursForFeiertag(data.hoursForFeiertag ? data.hoursForFeiertag.toFixed(2) : '0.00');

      let stundensatzJahre = typeof data.hourlyWage === 'string'
        ? JSON.parse(data.hourlyWage)
        : data.hourlyWage;

      if (stundensatzJahre[aktuellesJahr]) {
        setStundensatzJahr(stundensatzJahre[aktuellesJahr].stundensatz);
        setStundensatzFachgebiet(stundensatzJahre[aktuellesJahr].fachgebiet);
      }
    } catch (err) {
      toast.error(err.message || 'Fehler beim Laden der Einstellungen.');
    }
  };

  const fetchHistory = async (entryId) => {
    try {
      const data = await fetchAPI(`${API_URL}/entries/${entryId}/history`, {
        headers: { 'Authorization': `Bearer ${auth.token}` },
      }, 'Fehler beim Laden der Historie.');
      setHistoryRecords(data);
    } catch (err) {
      toast.error(err.message || 'Fehler beim Laden der Historie.');
    }
  };

  // CSV-Export
  const formatNumber = (number) => {
    return number.toFixed(2).replace('.', ',');
  };

  const prepareCSVData = () => {
    if (exportType === 'streit') {
      return entries.map(entry => ({
        MitarbNr: '00000001',
        Datum: entry.date,
        Uhrzeit: entry.startTime,
        Menge: formatNumber(entry.hours),
        Lohnart: (entry.type === 'urlaub' || entry.type === 'feiertag') ? '00001' : '01000',
        Zeitart: (entry.type === 'urlaub' || entry.type === 'feiertag') ? entry.type : 'Consulting',
        Einzelpreis: formatNumber(entry.hourlyWage),
      }));
    } else if (exportType === 'datev') {
      return entries.map(entry => ({
        Buchungsdatum: entry.date,
        Buchungstyp: entry.type,
        Beschreibung: entry.subject,
        Adresse: entry.address,
        Startzeit: entry.startTime,
        Endzeit: entry.endTime,
        Stunden: entry.hours,
        Hotelspeesen: Boolean(entry.hotelspeesen) ? '1' : '0',
        Verpflegungsmehraufwand: Boolean(entry.verpflegungsMehraufwand) ? '1' : '0',
      }));
    } else {
      return entries.map(entry => ({
        ID: entry.id,
        Datum: entry.date,
        Typ: entry.type,
        Betreff: entry.subject,
        Adresse: entry.address,
        Startzeit: entry.startTime,
        Endzeit: entry.endTime,
        Stunden: entry.hours,
        Hotelspeesen: Boolean(entry.hotelspeesen) ? 'Ja' : 'Nein',
        Verpflegungsmehraufwand: Boolean(entry.verpflegungsMehraufwand) ? 'Ja' : 'Nein',
        customerId: entry.customerId,
        hourlyWage: entry.hourlyWage,
        fachgebiet: entry.fachgebiet === 1 ? 'Workflow' : entry.fachgebiet === 2 ? 'Finanzbuchhaltung' : 'nein',
        homeoffice: Boolean(entry.homeoffice) ? 'Ja' : 'Nein',
        abgerechnet: Boolean(entry.abgerechnet) ? 'Ja' : 'Nein',
        aenderungsGrund: entry.aenderungsGrund,
        aenderungsText: entry.aenderungsText
      }));
    }
  };

  const headers = () => {
    if (exportType === 'streit') {
      return [
        'MitarbNr',
        'Datum',
        'Uhrzeit',
        'Menge',
        'Lohnart',
        'Zeitart',
        'Einzelpreis',
      ];
    } else if (exportType === 'datev') {
      return [
        'Buchungsdatum',
        'Buchungstyp',
        'Beschreibung',
        'Adresse',
        'Startzeit',
        'Endzeit',
        'Stunden',
        'Hotelspeesen',
        'Verpflegungsmehraufwand',
      ];
    } else {
      return [
        'ID',
        'Datum',
        'Typ',
        'Betreff',
        'Adresse',
        'Startzeit',
        'Endzeit',
        'Stunden',
        'Hotelspeesen',
        'Verpflegungsmehraufwand',
        'customerId',
        'hourlyWage',
        'fachgebiet',
        'homeoffice',
        'abgerechnet',
        'aenderungsGrund',
        'aenderungsText'
      ];
    }
  };

  const exportCSV = () => {
    const csvRows = [];
    const csvHeaders = headers().join(';');
    csvRows.push(csvHeaders);

    prepareCSVData().forEach(entry => {
      const row = headers()
        .map(header => {
          const value = entry[header];
          if (typeof value === 'string') {
            return value.replace(/"/g, '').replace(/;/g, ',');
          }
          return value;
        })
        .join(';');
      csvRows.push(row);
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      exportType === 'streit'
        ? 'std.csv'
        : `stundenbuchungen_${exportType}_${format(new Date(), 'yyyy-MM-dd')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handler für Aktionen in der Tabelle
  const handleAbrechnen = async (id) => {
    if (!window.confirm('Wurde die Stundenbuchung abgerechnet?')) return;
    try {
      const entryToUpdate = entries.find((entry) => entry.id === id);
      if (!entryToUpdate) {
        toast.error('Eintrag nicht gefunden.');
        return;
      }
      const updatedEntry = { ...entryToUpdate, abgerechnet: true };
      const data = await fetchAPI(`${API_URL}/entries/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`,
        },
        body: JSON.stringify(updatedEntry),
      }, 'Fehler beim Aktualisieren des Eintrags.');
      setEntries(prev =>
        prev.map((entry) => (entry.id === data.id ? data : entry))
      );
      toast.success('Eintrag erfolgreich abgerechnet.');
    } catch (err) {
      toast.error(err.message || 'Serverfehler.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diesen Eintrag wirklich löschen?')) return;
    try {
      await fetchAPI(`${API_URL}/entries/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${auth.token}` },
      }, 'Fehler beim Löschen des Eintrags.');
      setEntries(prev => prev.filter(entry => entry.id !== id));
      toast.success('Eintrag erfolgreich gelöscht.');
    } catch (err) {
      toast.error(err.message || 'Serverfehler.');
    }
  };

  // Beim Bearbeiten einen Eintrag in den Modal laden
  const handleEdit = (entry) => {
    setEditingEntry(entry);
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
  };

  // Beim Abspeichern im Modal
  const handleUpdate = async (updatedData) => {
    try {
      const data = await fetchAPI(`${API_URL}/entries/${editingEntry.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`,
        },
        body: JSON.stringify(updatedData),
      }, 'Fehler beim Aktualisieren des Eintrags.');
      setEntries(prev => prev.map(entry => (entry.id === data.id ? data : entry)));
      toast.success('Eintrag erfolgreich aktualisiert.');
      setEditingEntry(null);
    } catch (err) {
      toast.error(err.message || 'Serverfehler.');
    }
  };

  const handleShowHistory = (entryId) => {
    setSelectedEntryId(entryId);
    fetchHistory(entryId);
    setShowHistoryModal(true);
  };

  // Feiertage speichern
  const handleSaveFeiertage = async (formattedData) => {
    try {
      const postData = await fetchAPI(`${API_URL}/feiertage/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`,
        },
        body: JSON.stringify({
          feiertage: formattedData,
          stundensatz: stundensatzJahr,
          hoursForFeiertag: hoursForFeiertag,
        }),
      }, 'Fehler beim Übertragen der Feiertage');

      if (postData.status === 1) {
        toast.success('Feiertage erfolgreich aktualisiert.');
        fetchEntries(); // Aktualisiere Einträge, um neue Feiertage anzuzeigen
      }
    } catch (err) {
      toast.error(err.message || 'Serverfehler.');
    }
  };

  // Memoisierte Kundenoptionen für react-select
  const customerOptions = useMemo(() => {
    return customers.map(c => ({
      value: c.id,
      label: `${c.vorname || ''} ${c.nachname || ''}`.trim(),
    }));
  }, [customers]);

  // Filtere Einträge anhand der Suchanfrage
  const filteredEntries = useMemo(() => {
    return entries.filter(entry =>
      entry.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.date?.includes(searchTerm)
    );
  }, [entries, searchTerm]);

  // Hilfsobjekt, um Kundendaten (z. B. Nachname) schnell zuzuordnen
  const customerMap = useMemo(() => {
    return customers.reduce((map, cust) => {
      map[cust.id] = cust;
      return map;
    }, {});
  }, [customers]);

  // Typ-Abkürzungen
  const getTypeShortcut = (type) => {
    switch (type) {
      case 'urlaub': return 'U';
      case 'feiertag': return 'F';
      case 'normal': return 'N';
      case 'krank': return 'K';
      case 'halb-vormittag': return 'HV';
      case 'halb-nachmittag': return 'HN';
      default: return '?';
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Alle Stundenbuchungen</h2>

      {/* Obere Leiste: Suche, Feiertage und Export */}
      <div style={styles.topContainer}>
        <input
          type="text"
          placeholder="Suche nach Betreff, Typ oder Datum..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
        <button onClick={() => setShowFeiertageDialog(true)} style={styles.feiertagButton}>
          Feiertage generieren, aktuelles Jahr
        </button>
        
        <div style={styles.exportContainer}>
          <label htmlFor="exportType" style={styles.exportLabel}>Export-Typ:</label>
          <select
            id="exportType"
            value={exportType}
            onChange={(e) => setExportType(e.target.value)}
            style={styles.exportSelect}
          >
            <option value="standard">CSV</option>
            <option value="streit">Streit</option>
            <option value="datev">Datev</option>
          </select>
          <button onClick={exportCSV} style={styles.exportBtn}>Exportieren</button>
        </div>
      </div>

      {/* Tabelle der Einträge */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Datum</th>
              <th style={styles.th}>Art</th>
              <th style={styles.th}>Betreff</th>
              <th style={styles.th}>Kunde</th>
              <th style={styles.th}>Startzeit</th>
              <th style={styles.th}>Endzeit</th>
              <th style={styles.th}>Stunden</th>
              <th style={styles.th}>Fachgebiet</th>
              <th style={styles.th}>Speesen</th>
              <th style={styles.th}>Verpflegung</th>
              <th style={styles.th}>Homeoffice</th>
              <th style={styles.th}>Abgerechnet</th>
              <th style={styles.th}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filteredEntries.map(entry => (
              <tr key={entry.id}>
                <td style={styles.td}>{formatDateToGerman(parseDate(entry.date))}</td>
                <td style={styles.td}>{getTypeShortcut(entry.type)}</td>
                <td style={styles.td}>{entry.subject}</td>
                <td style={styles.td}>{customerMap[entry.customerId]?.nachname ?? ''}</td>
                <td style={styles.td}>{entry.startTime}</td>
                <td style={styles.td}>{entry.endTime}</td>
                <td style={styles.td}>{parseFloat(entry.hours).toFixed(2)}</td>
                <td style={styles.td}>{entry.fachgebiet === 1 ? 'Workflow' : entry.fachgebiet === 2 ? 'Finanzbuchhaltung' : ""}</td>
                <td style={styles.td}>{entry.hotelspeesen === true || entry.hotelspeesen === 1 ? 'Ja' : ''}</td>
                <td style={styles.td}>{entry.verpflegungsMehraufwand === true || entry.verpflegungsMehraufwand === 1 ? 'Ja' : ''}</td>
                <td style={styles.td}>{entry.homeoffice === true || entry.homeoffice === 1 ? 'Ja' : ''}</td>
                <td style={styles.td}>{entry.abgerechnet === true || entry.abgerechnet === 1 ? 'Ja' : ''}</td>
                <td style={styles.td}>
                  <button onClick={() => handleAbrechnen(entry.id)} style={styles.iconButton} title="Als abgerechnet markieren">💰</button>
                  <button onClick={() => handleEdit(entry)} style={styles.iconButton} title="Bearbeiten">✏️</button>
                  <button onClick={() => handleShowHistory(entry.id)} style={styles.iconButton} title="Historie anzeigen">📋</button>
                  <button onClick={() => handleDelete(entry.id)} style={styles.iconButton} title="Löschen">🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bearbeitungsmodal */}
      {editingEntry && (
        <EditEntryModal
          entry={editingEntry}
          onCancel={handleCancelEdit}
          onUpdate={handleUpdate}
          onShowHistory={() => handleShowHistory(editingEntry.id)}
          stundensatzJahr={stundensatzJahr}
          stundensatzFachgebiet={stundensatzFachgebiet}
          customerOptions={customerOptions}
        />
      )}

      {/* Feiertage Dialog */}
      {showFeiertageDialog && (
        <FeiertageDialog
          onCancel={() => setShowFeiertageDialog(false)}
          onSave={handleSaveFeiertage}
          aktuellesJahr={aktuellesJahr}
        />
      )}

      {/* Historie-Modal */}
      {showHistoryModal && (
        <HistoryModal
          historyRecords={historyRecords}
          entryId={selectedEntryId}
          onClose={() => setShowHistoryModal(false)}
        />
      )}
    </div>
  );
}

export default EntriesList;